import items from "./items";
import { SPECIALS } from "./specials";
import { getFortressBonuses } from "./upgrades";

export const UNIT_STATS = {
  soldier: {
    health: 48,
    speed: 9,
    attack: 32,
    range: 1,
    cost: 45,
    stamina: 55,
    abilities: ["attack", "sprint", "meditation", "collect", "mount"],
    specials: ["stun", "rage"],
    startingSpecials: 1,
    description: "Basic unit that deals good melee damage.",
  },
  archer: {
    health: 40,
    speed: 9,
    attack: 17,
    range: 2,
    cost: 55,
    stamina: 60,
    abilities: ["shoot", "sprint", "meditation", "collect", "mount"],
    specials: ["leap", "knock_down", "poison_shot"],
    startingSpecials: 1,
    description:
      "Like soldier but can shoot targets up to 18 inches away. Less damage.",
  },
  fortress: {
    health: 68,
    speed: 0,
    attack: 0,
    range: 0,
    cost: 100,
    stamina: 0,
    abilities: [],
  },
  wizard: {
    health: 33,
    speed: 7,
    attack: 0,
    range: 3,
    cost: 55,
    stamina: 65,
    castingRange: 5,
    abilities: ["heal", "sprint", "meditation", "collect", "mount"],
    specials: ["enlighten", "teleport", "empower", "aura_of_protection"],
    startingSpecials: 2,
    requiresUpgrade: true,
    description:
      "Can heal and teleport other units. No attack and less health.",
  },
  necromancer: {
    health: 38,
    speed: 7,
    attack: 18,
    range: 2,
    cost: 60,
    stamina: 60,
    castingRange: 5,
    abilities: ["attack", "sprint", "meditation", "collect", "mount"],
    specials: ["revive", "spawn", "mind_control"],
    startingSpecials: 2,
    description:
      "Dark caster that raises skeletons and bends enemies to their will.",
  },
  kamikaze: {
    health: 22,
    speed: 18,
    attack: 42,
    range: 1,
    cost: 55,
    stamina: 55,
    abilities: ["kamikaze", "meditation"],
    specials: ["split_self", "back_off"],
    requiresUpgrade: true,
    startingSpecials: 1,
    description:
      "Can only attack by blowing up itself and everything nearby for TONS of damage.",
  },
  giant: {
    health: 63,
    speed: 7,
    attack: 45,
    range: 1,
    cost: 60,
    stamina: 55,
    abilities: ["attack", "sprint", "meditation", "collect"],
    specials: ["hook", "toss", "circle_of_pain"],
    requiresUpgrade: true,
    startingSpecials: 1,
    description: "Slow to move but deals a lot of damage.",
  },
  flagbearer: {
    health: 33,
    speed: 9,
    attack: 12,
    range: 1,
    cost: 40,
    stamina: 50,
    abilities: ["attack", "sprint", "meditation", "collect", "mount"],
    specials: ["battle_cry", "back_off", "rally_the_troops", "fortify"],
    startingSpecials: 2,
    description:
      "Support unit. Gets two special abilities. Has a weak attack and less health. When using Fortresses, units can be spawned wherever your flagbearer is.",
  },
  skeleton: {
    health: 10,
    speed: 9,
    attack: 15,
    range: 1,
    cost: 20,
    stamina: 50,
    abilities: ["attack"],
    specials: [],
    startingSpecials: 0,
    description: "Summoned undead fighter with low health and modest damage.",
  },
};

// All the basic abilities
export const ABILITIES = {
  flag: {
    stamina: 40,
    name: "Plant the flag",
    altName: "Pull up the flag",
    type: "toggle",
    gameState: "confirmFlag",
  },
  attack: {
    stamina: 35,
    gameState: "chooseAttackTarget",
  },
  shoot: {
    stamina: 30,
    gameState: "chooseShootTarget",
  },
  heal: {
    stamina: 50,
    gameState: "chooseHealTarget",
  },
  teleport: {
    stamina: 60,
    type: "instant",
    gameState: undefined,
  },
  collect: {
    // Combined "Collect" action:
    // - Picking up an item costs 40 stamina
    // - Collecting a treasure chest costs 40 stamina
    // The sidebar enforces the per-option stamina/rules.
    stamina: 40,
    gameState: "chooseCollectTarget",
    cannotBeIn: ["inCombat", "mounted"],
  },
  kamikaze: {
    stamina: 50,
    gameState: "chooseKamikazeTargets",
    explosionRange: 4, // inches
  },
  meditation: {
    stamina: 45,
    name: "Meditate",
    type: "instant",
    cannotBeIn: ["inCombat"],
  },
  generateGold: {
    stamina: 45,
    name: "Generate gold",
    gameState: "chooseGenerateGoldTarget",
  },
  refreshStamina: {
    stamina: 50,
    name: "Refresh stamina",
    gameState: "chooseRefreshStaminaTarget",
  },
  grantExperience: {
    stamina: 55,
    name: "Grant experience",
    gameState: "chooseGrantExperienceTarget",
  },
  mount: {
    stamina: 30,
    name: "Mount",
    type: "instant",
    cannotBeIn: ["inCombat"],
  },
  dismount: {
    stamina: 25,
    name: "Dismount",
    type: "instant",
  },
  sprint: {
    stamina: 40,
    name: "Sprint",
    type: "instant",
    cannotBeIn: ["mounted", "inCombat"],
  },
  leaveCombat: {
    stamina: 0,
    name: "Leave combat",
    type: "instant",
  },
  flee: {
    stamina: 40,
    name: "Flee",
    gameState: "rollForFlee",
  },
};

// --- Close combat (melee) tracking ---
// Stored as string entries inside Character.state[]: "combat:<characterId>"
export const COMBAT_STATE_PREFIX = "combat:";
export const MIND_CONTROLLED_STATE_PREFIX = "mind_controlled:";

export function getCombatOpponentIdsFromState(state = []) {
  if (!Array.isArray(state) || state.length === 0) return [];
  const ids = [];
  for (const s of state) {
    if (typeof s !== "string") continue;
    if (!s.startsWith(COMBAT_STATE_PREFIX)) continue;
    const raw = s.slice(COMBAT_STATE_PREFIX.length);
    const id = Number.parseInt(raw, 10);
    if (Number.isFinite(id)) ids.push(id);
  }
  return ids;
}

export function isInCombatState(state = []) {
  return getCombatOpponentIdsFromState(state).length > 0;
}

export function addCombatOpponentToState(state = [], opponentId) {
  const current = Array.isArray(state) ? state : [];
  const tag = `${COMBAT_STATE_PREFIX}${opponentId}`;
  if (current.includes(tag)) return current;
  return [...current, tag];
}

export function removeCombatOpponentFromState(state = [], opponentId) {
  const current = Array.isArray(state) ? state : [];
  const tag = `${COMBAT_STATE_PREFIX}${opponentId}`;
  return current.filter((s) => s !== tag);
}

export function clearCombatFromState(state = []) {
  const current = Array.isArray(state) ? state : [];
  return current.filter(
    (s) => !(typeof s === "string" && s.startsWith(COMBAT_STATE_PREFIX)),
  );
}

export function getMindControlledOriginalPlayerId(state = []) {
  if (!Array.isArray(state) || state.length === 0) return null;
  const tag = state.find(
    (s) => typeof s === "string" && s.startsWith(MIND_CONTROLLED_STATE_PREFIX),
  );
  if (!tag) return null;
  const raw = tag.slice(MIND_CONTROLLED_STATE_PREFIX.length);
  const id = Number.parseInt(raw, 10);
  return Number.isFinite(id) ? id : null;
}

export function removeMindControlledState(state = []) {
  const current = Array.isArray(state) ? state : [];
  return current.filter(
    (s) =>
      !(typeof s === "string" && s.startsWith(MIND_CONTROLLED_STATE_PREFIX)),
  );
}

// Attack strength modifiers for attack and shoot actions
export const ATTACK_STRENGTHS = {
  light: {
    staminaModifier: -5, // (30)
    damageModifier: -0.3, // -% max damage potential
  },
  standard: {
    staminaModifier: 10, // (35 + 10 = 45)
    damageModifier: 0, // Regular damage
  },
  strong: {
    staminaModifier: 25, // (35 + 20 = 55)
    damageModifier: 0.2, // +% max damage potential
  },
};

/**
 * Calculate the required experience for a character to level up
 * @param {Number} level - Current character level
 * @returns {Number} Experience required to reach the next level
 */
export function calculateRequiredExperience(level) {
  return 40 + level * 20;
}

/**
 * Calculate a character's max health based on level and type
 * @param {String} characterType - The type of character
 * @param {Number} level - The character's level
 * @param {Array} unlockedUpgrades - Array of unlocked upgrade keys (for fortress bonuses)
 * @returns {Number} The max health value
 */
export function calculateMaxHealth(
  characterType,
  level,
  unlockedUpgrades = [],
) {
  const baseHealth = UNIT_STATS[characterType].health;
  let maxHealth = baseHealth * 1.1 ** (level - 1);

  // Add fortress upgrade bonuses
  if (characterType === "fortress") {
    maxHealth += calculateFortressHealthBonus(unlockedUpgrades);
  }

  return Math.floor(maxHealth);
}

/**
 * Calculate a character's max stamina based on level and type
 * @param {String} characterType - The type of character
 * @param {Number} level - The character's level
 * @returns {Number} The max stamina value
 */
export function calculateMaxStamina(characterType, level) {
  const baseStamina = UNIT_STATS[characterType].stamina;
  const maxStamina = baseStamina * 1.15 ** (level - 1);
  return Math.floor(maxStamina);
}

/**
 * Calculate a character's attack based on level and type
 * @param {String} characterType - The type of character
 * @param {Number} level - The character's level
 * @returns {Number} The attack value
 */
export function calculateAttack(characterType, level, baseAttack = null) {
  const attackValue =
    baseAttack !== null ? baseAttack : UNIT_STATS[characterType].attack;
  const maxAttack = attackValue * 1.15 ** (level - 1);
  return Math.floor(maxAttack);
}

/**
 * Applies passive item benefits to a character's stats
 * @param {Object} character - The character object
 * @param {Array} allItems - All available items in the game
 * @param {Array} unlockedUpgrades - Array of unlocked upgrade keys (for fortress bonuses)
 * @returns {Object} Modified character with applied item benefits
 */
export function applyItemBenefits(character, allItems, unlockedUpgrades = []) {
  // Create a deep copy of the character to avoid modifying the original
  const modifiedCharacter = JSON.parse(JSON.stringify(character));

  // Initialize maxHealth property with the base calculated value
  modifiedCharacter.maxHealth = calculateMaxHealth(
    modifiedCharacter.type,
    modifiedCharacter.level,
    unlockedUpgrades,
  );

  // Initialize maxStamina property with the base calculated value
  modifiedCharacter.maxStamina = calculateMaxStamina(
    modifiedCharacter.type,
    modifiedCharacter.level,
  );

  // Mounted state effects: double speed while mounted
  // FIXME: Should state effects be handled in a separate function?
  if (modifiedCharacter.state?.includes("mounted")) {
    modifiedCharacter.speed = modifiedCharacter.speed * 2;
  }

  // Apply each item's benefits to the character
  for (const itemName of modifiedCharacter.items) {
    const item = allItems.find((i) => i.name === itemName);

    if (!item || !item.benefits) continue;

    // Apply max stamina bonus from stamina items like Staff of Power
    if (item.benefits.stamina) {
      modifiedCharacter.maxStamina += item.benefits.stamina;
    }

    // Apply damage bonus
    if (item.benefits.damage) {
      modifiedCharacter.attack += item.benefits.damage;
    }

    // Apply defense bonus (not directly visible but will be used in combat)
    if (item.benefits.defense) {
      modifiedCharacter.defense =
        (modifiedCharacter.defense || 0) + item.benefits.defense;
    }

    // Apply speed/range bonus
    if (item.benefits.range) {
      // Mounted characters don't get range bonuses
      if (modifiedCharacter.state?.includes("mounted")) continue;

      modifiedCharacter.speed += item.benefits.range;
    }

    // Apply health bonus from Ring of Health
    if (item.benefits.health) {
      // Apply the bonus to max health.
      // NOTE: Do not mutate current health here. Current health is authoritative
      // in the DB and should only change via explicit game actions (heal/damage/collect).
      // Mutating it here causes client/server mismatches (e.g. log damage != HP delta).
      modifiedCharacter.maxHealth += item.benefits.health;
    }

    // Apply magic resistance from Helmet of Stability
    if (item.benefits.resistance) {
      modifiedCharacter.resistance = item.benefits.resistance;
    }

    // Apply attack range increase for flail
    if (item.benefits.attackRange) {
      modifiedCharacter.attackRange =
        (modifiedCharacter.attackRange || 0) + item.benefits.attackRange;
    }

    // Apply distance roll modifier for archers
    if (
      item.benefits.distanceRollModifier &&
      modifiedCharacter.type === "archer"
    ) {
      modifiedCharacter.distanceRollModifier =
        (modifiedCharacter.distanceRollModifier || 0) +
        item.benefits.distanceRollModifier;
    }

    if (item.benefits.canLeaveCombat) {
      modifiedCharacter.canLeaveCombat = true;
    }
  }

  // Special ability effects
  // FIXME: Should this be looping through the special abilities instead, just like items?
  // FIXME: Or else this list might get very long...
  if (modifiedCharacter.state?.includes("battle_cry_buff")) {
    modifiedCharacter.attack += SPECIALS["battle_cry"].benefits.attack;
  }

  return modifiedCharacter;
}

/**
 * Helper function to get all enhanced character stats in one place
 * @param {Object} character - The character object
 * @returns {Object} Object containing enhanced character and calculated stats
 */
export function getEnhancedCharacterStats(character, unlockedUpgrades = []) {
  if (!character) return null;

  // Calculate base stats
  const baseMaxHealth = calculateMaxHealth(
    character.type,
    character.level,
    unlockedUpgrades,
  );
  const baseMaxStamina = calculateMaxStamina(character.type, character.level);
  const requiredExp = calculateRequiredExperience(character.level);

  // Apply item benefits (pass unlockedUpgrades so fortress upgrades are included)
  const enhancedCharacter = applyItemBenefits(
    character,
    items,
    unlockedUpgrades,
  );

  // Get final max health (with item benefits)
  const maxHealth = enhancedCharacter.maxHealth || baseMaxHealth;

  // Get final max stamina (with item benefits)
  const maxStamina = enhancedCharacter.maxStamina || baseMaxStamina;

  // Current health (enhanced if applicable)
  const currentHealth = enhancedCharacter.health || character.health;
  const currentStamina = enhancedCharacter.stamina || character.stamina;

  return {
    enhancedCharacter,
    baseMaxHealth,
    baseMaxStamina,
    maxHealth,
    maxStamina,
    currentHealth,
    currentStamina,
    requiredExp,
  };
}

/**
 * Calculate the total dodge bonus for a character from their items
 * @param {Object} character - The character object
 * @returns {Number} The total dodge bonus (0 if no bonuses)
 */
export function getDodgeBonus(character) {
  let dodgeBonus = 0;

  // Check for items that provide dodge bonuses
  if (character.items && character.items.length > 0) {
    for (const itemName of character.items) {
      const item = items.find((i) => i.name === itemName);
      if (!item || !item.benefits) continue;

      // Add dodge bonus
      if (item.benefits.dodgeBonus) {
        dodgeBonus += item.benefits.dodgeBonus;
      }
    }
  }

  return dodgeBonus;
}

/**
 * Calculate damage reduction from a dodge roll
 * @param {Number} roll - The dice roll (1-6)
 * @param {Number} dodgeBonus - Bonus to add to roll
 * @param {Number} originalDamage - The original damage amount
 * @returns {Object} Object with damage reduction details
 */
export function calculateDodgeResult(roll, dodgeBonus, originalDamage) {
  const finalRoll = Math.min(6, roll + dodgeBonus);

  let damageReduction = 0;
  let dodgeType = "failed";

  if (finalRoll === 6) {
    // Complete dodge
    damageReduction = originalDamage;
    dodgeType = "complete";
  } else if (finalRoll === 5) {
    // 30% damage reduction
    damageReduction = Math.floor(originalDamage * 0.3);
    dodgeType = "partial";
  } else if (finalRoll === 4) {
    // No damage reduction
    damageReduction = 0;
    dodgeType = "failed";
  }

  return {
    roll: finalRoll,
    originalRoll: roll,
    dodgeBonus,
    damageReduction,
    dodgeType,
    finalDamage: originalDamage - damageReduction,
  };
}

/**
 * Calculate fortress health bonus based on unlocked upgrades
 * @param {Array} unlockedUpgrades - Array of unlocked upgrade keys
 * @returns {Number} Total health bonus from fortress upgrades
 */
export function calculateFortressHealthBonus(unlockedUpgrades) {
  const { healthBonus } = getFortressBonuses(unlockedUpgrades);
  return healthBonus;
}

/**
 * Get human-readable labels for a character's current state flags
 * @param {Object} character - The character object
 * @returns {Array<string>} Array of state label strings
 */
export function getCharacterStateLabels(character) {
  if (!character || !character.state || character.state.length === 0) {
    return [];
  }

  const labels = [];

  if (character.state.includes("stunned")) {
    labels.push("Stunned");
  }

  if (character.state.includes("meditated")) {
    labels.push("Meditated");
  }

  if (
    character.state.some(
      (s) =>
        typeof s === "string" && s.startsWith(MIND_CONTROLLED_STATE_PREFIX),
    )
  ) {
    labels.push("Mind controlled");
  }

  if (character.state.includes("battle_cry_buff")) {
    labels.push("Battle Cried");
  }

  if (character.state.includes("aura_of_protection")) {
    labels.push("Aura of Protection");
  }

  if (character.state.includes("mounted")) {
    labels.push("Mounted");
  }

  // Check for poisoned state (format: poisoned:<archerId>:<turnsRemaining>)
  const poisonState = character.state.find((s) => s.startsWith("poisoned:"));
  if (poisonState) {
    const turnsRemaining = parseInt(poisonState.split(":")[2], 10);
    labels.push(`Poisoned (${turnsRemaining})`);
  }

  const combatIds = getCombatOpponentIdsFromState(character.state);
  if (combatIds.length > 0) {
    labels.push(
      combatIds.length === 1 ? "In Combat" : `In Combat (${combatIds.length})`,
    );
  }

  return labels;
}

/**
 * Get a comma-separated text for a character's state flags
 * @param {Object} character - The character object
 * @returns {string} Comma separated state labels
 */
export function getCharacterStateText(character) {
  return getCharacterStateLabels(character).join(", ");
}
